import React, { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { Dropdown, Table, ButtonGroup, Button } from "react-bootstrap";
import ExportButton from "./ExportButton";
import { exportToExcel } from "./exportExcel";

const HospitalProcedure = props => {
    const {
        className,
        innerRef,
        hospitalSummary,
        ...attributes
    } = props;

    const [hospitalList, setHospitalList] = useState([]);
    const [options, setOptions] = useState({
        chart: {
            id: "assetDistribution",
            type: "bar",
            height: 350,
            toolbar: { show: false }
        },
        plotOptions: {
            bar: {
                horizontal: true,
                endingShape: "rounded",
                barHeight: "65%",
                borderRadius: 5
            }
        },
        colors: ["#77248B"],
        dataLabels: { enabled: false },
        legend: { show: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        grid: { borderColor: "rgba(252, 252, 252,0.2)" },
        xaxis: {
            categories: [],
            labels: {
                style: {
                    colors: "#000000",
                    fontSize: "12px",
                    fontFamily: "poppins",
                    fontWeight: 400,
                    cssClass: "apexcharts-xaxis-label"
                }
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
            crosshairs: { show: false }
        },
        yaxis: {
            labels: {
                style: {
                    colors: "#000000",
                    fontSize: "12px",
                    fontFamily: "poppins",
                    fontWeight: 400,
                    cssClass: "apexcharts-yaxis-label"
                }
            }
        },
        tooltip: { y: { formatter: val => val } },
        responsive: [
            {
                breakpoint: 575,
                options: { chart: { height: 300 }, plotOptions: { bar: { barHeight: "50%" } } }
            }
        ]
    });

    const [series, setSeries] = useState([]);
    const [seriesTotal, setSeriesTotal] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [selectBtn, setSelectBtn] = useState("Geral");
    const [viewMode, setViewMode] = useState("table");

    useEffect(() => {
        const hospitalSet = new Set(
            hospitalSummary.map(item => `${item.hospital_id}-${item.hospital_name}`)
        );
        const hospitalArray = Array.from(hospitalSet).map(item => {
            const [hospital_id, hospital_name] = item.split("-");
            return { hospital_id, hospital_name };
        });
        const hospitalSorted = hospitalArray.sort((a, b) =>
            String(a.hospital_name).localeCompare(String(b.hospital_name))
        );
        setHospitalList(hospitalSorted);

        changeSeries([...hospitalSummary]);
    }, [hospitalSummary]);

    function changeSeries(dataArray) {
        let newSeries = [];
        let totalSeries = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        for (let indexSummary = 0; indexSummary < dataArray.length; indexSummary++) {
            const indexSeries = newSeries.findIndex(
                x => x.hospital_id === dataArray[indexSummary].hospital_id
            );

            if (indexSeries < 0) {
                newSeries.push({
                    hospital_id: dataArray[indexSummary].hospital_id,
                    hospital_name: dataArray[indexSummary].hospital_name,
                    quantity: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
                });
                newSeries[newSeries.length - 1].quantity[dataArray[indexSummary].month - 1] =
                    dataArray[indexSummary].count;
                newSeries[newSeries.length - 1].quantity[12] = dataArray[indexSummary].count;
            } else {
                newSeries[indexSeries].quantity[dataArray[indexSummary].month - 1] +=
                    dataArray[indexSummary].count;
                newSeries[indexSeries].quantity[12] += dataArray[indexSummary].count;
            }

            totalSeries[dataArray[indexSummary].month - 1] += dataArray[indexSummary].count;
            totalSeries[12] += dataArray[indexSummary].count;
        }

        const seriesSorted = newSeries.sort((a, b) =>
            String(a.hospital_name).localeCompare(String(b.hospital_name))
        );

        setSeries(seriesSorted);
        setSeriesTotal(totalSeries);
    }

    function handleChangeHospital(id) {
        if (id !== "Geral") {
            const filter = id === "Complexa" ? "COM" : "NOR";
            const hospitalSummaryFiltered = hospitalSummary.filter(x => x.complexity === filter);
            changeSeries([...hospitalSummaryFiltered]);
        } else {
            changeSeries([...hospitalSummary]);
        }

        setSelectBtn(id);
    }

    const chartData = useMemo(() => {
        if (!series || series.length === 0) return { categories: [], data: [] };

        const sorted = [...series].sort((a, b) => b.quantity[12] - a.quantity[12]);
        const categories = sorted.map(s => s.hospital_name || "Hospital não informado");
        const data = sorted.map(s => s.quantity[12]);

        return { categories, data };
    }, [series]);

    const chartOptions = useMemo(() => {
        return {
            ...options,
            xaxis: { ...options.xaxis, categories: chartData.categories },
            title: {
                text: "Total de procedimentos por hospital",
                style: { fontFamily: "poppins", fontWeight: 600 }
            }
        };
    }, [options, chartData.categories]);

    const chartSeries = useMemo(() => [{ name: "Procedimentos", data: chartData.data }], [chartData.data]);

    function handleExport() {
        const months = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
        const toRow = (name, counter) => {
            const row = { Hospital: name };
            months.forEach((month, i) => { row[month] = counter[i]; });
            row.TOTAL = counter[12];
            row["%"] = seriesTotal[12] > 0 ? Number(((counter[12] / seriesTotal[12]) * 100).toFixed(2)) : 0;
            return row;
        };

        const rows = series.map(seriesItem => toRow(seriesItem.hospital_name || "Hospital não informado", seriesItem.quantity));
        rows.push(toRow("TOTAL GERAL", seriesTotal));
        exportToExcel(rows, "Hospitais", "procedimentos_por_hospital");
    }

    return (
        <>
            <div className="row">
                <div className="col-xl-8">
                    <div className="card-header border-0 flex-wrap pb-2">
                        <div className="chart-title mb-2 ">
                            <h2 className="heading">Procedimentos por Hospital</h2>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="d-flex justify-content-end gap-2 align-items-center">
                        <Dropdown className="me-2 drop-select">
                            <Dropdown.Toggle as="div" className="i-false drop-select-btn style-dark" style={{ backgroundColor: "#fff" }}>
                                {selectBtn} <i className="fa-solid fa-angle-down"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item key="Geral" onClick={() => handleChangeHospital("Geral")}>
                                    Geral
                                </Dropdown.Item>
                                <Dropdown.Item key="Complexa" onClick={() => handleChangeHospital("Complexa")}>
                                    Complexa
                                </Dropdown.Item>
                                <Dropdown.Item key="Convencional" onClick={() => handleChangeHospital("Convencional")}>
                                    Convencional
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <ButtonGroup aria-label="View Mode">
                            <Button
                                variant={viewMode === "table" ? "primary" : "light"}
                                onClick={() => setViewMode("table")}
                            >
                                Tabela
                            </Button>
                            <Button
                                variant={viewMode === "chart" ? "primary" : "light"}
                                onClick={() => setViewMode("chart")}
                            >
                                Indicador
                            </Button>
                        </ButtonGroup>
                        <ExportButton onClick={handleExport} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="card-body pt-0 custome-tooltip">
                        {viewMode === "table" ? (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>
                                            <strong>HOSPITAL</strong>
                                        </th>
                                        {[
                                            "JAN",
                                            "FEV",
                                            "MAR",
                                            "ABR",
                                            "MAI",
                                            "JUN",
                                            "JUL",
                                            "AGO",
                                            "SET",
                                            "OUT",
                                            "NOV",
                                            "DEZ"
                                        ].map(m => (
                                            <th key={m} className="text-center">
                                                <strong>{m}</strong>
                                            </th>
                                        ))}
                                        <th className="text-center">
                                            <strong>TOTAL</strong>
                                        </th>
                                        <th className="text-center">
                                            <strong>%</strong>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {series.map(seriesItem => {
                                        const counter = seriesItem.quantity;
                                        let hospitalPercent = 0;

                                        if (seriesTotal[12] > 0) {
                                            hospitalPercent = ((counter[12] / seriesTotal[12]) * 100).toFixed(2);
                                        }

                                        return (
                                            <tr key={seriesItem.hospital_id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="w-space-no">{seriesItem.hospital_name || "Hospital não informado"}</span>
                                                    </div>
                                                </td>
                                                {Array.from({ length: 12 }).map((_, i) => (
                                                    <td key={i}>
                                                        <div className="text-center">{counter[i]}</div>
                                                    </td>
                                                ))}
                                                <td>
                                                    <div className="text-center">
                                                        <strong>{counter[12]}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="text-end">{Number(hospitalPercent).toLocaleString("pt-br", { minimumFractionDigits: 2 })} %</div>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    <tr key="TOTGER">
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <span className="w-space-no">
                                                    <strong>TOTAL GERAL</strong>
                                                </span>
                                            </div>
                                        </td>
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <td key={i}>
                                                <div className="text-center">
                                                    <strong>{seriesTotal[i]}</strong>
                                                </div>
                                            </td>
                                        ))}
                                        <td>
                                            <div className="text-center">
                                                <strong>{seriesTotal[12]}</strong>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="text-end">
                                                <strong>100,00 %</strong>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={380} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

HospitalProcedure.propTypes = {
    children: PropTypes.node,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    hospitalSummary: PropTypes.array
};

HospitalProcedure.defaultProps = {
    hospitalSummary: []
};

export default HospitalProcedure;